import type { ViewerRole } from '../components/EngagementHub/roles';
import {
  canConfirmHoursFor,
  canResolveDisputeFor,
  canSubmitHoursFor,
  viewerSeesExpertRate,
} from '../components/EngagementHub/roles';
import Section from './Section';
import CurrentPeriodRow from './hours/CurrentPeriodRow';
import HoursRow from './hours/HoursRow';
import HoursTable from './hours/HoursTable';
import { partyIdentity } from './parties';
import { useSelfIdentity } from './session';
import type { EngagementView, HoursSubmissionView } from './viewModel';

interface HoursPanelProps {
  view: EngagementView;
  viewerRole: ViewerRole;
  viewerUserId: string;
}

/**
 * The hours ledger and everything that can be done to a row.
 *
 * The api appends one render-only `not_yet_submitted` row while the engagement
 * can accept hours (`engagementAcceptsHours` — active or closing). For the
 * expert it becomes the first row, `CurrentPeriodRow`, which opens the submit
 * form; for anyone else it is nothing. It has no submission behind it, so it
 * never joins the ledger or its sort.
 *
 * The ledger reads newest first, on both sides: the api sends it chronologically,
 * which buried the row awaiting action under every settled one.
 *
 * The Paid column is the expert's alone: when OnFrontiers paid the expert is
 * expert-side money, like the expert's rate (§6.3).
 */
export default function HoursPanel({
  view,
  viewerRole,
  viewerUserId,
}: HoursPanelProps): JSX.Element {
  const submitted = view.hoursSubmissions
    .filter((row) => row.status !== 'not_yet_submitted')
    .sort(newestFirst);
  const current = canSubmitHoursFor(view.status, viewerRole)
    ? view.hoursSubmissions.find((row) => row.status === 'not_yet_submitted')
    : undefined;
  const canConfirm = canConfirmHoursFor(view.status, viewerRole);
  const canResolve = canResolveDisputeFor(view.status, viewerRole);
  const showPaid = viewerSeesExpertRate(viewerRole);
  const self = useSelfIdentity();
  const identify = partyIdentity(view, viewerUserId, [], self);
  const expertName = (view.expertId && identify(view.expertId)?.name) || 'The expert';
  const nteRemaining = nteHeadroom(view);

  return (
    <Section title="Hours">
      {submitted.length === 0 && !current ? (
        <p className="text-on-surface-muted body-2">No hours have been submitted yet.</p>
      ) : (
        <HoursTable showPaid={showPaid}>
          {current ? (
            <CurrentPeriodRow row={current} engagementId={view.id} showPaid={showPaid} />
          ) : null}
          {submitted.map((row) => (
            <HoursRow
              key={row.id}
              row={row}
              showPaid={showPaid}
              identify={identify}
              canConfirm={canConfirm}
              canResolve={canResolve}
              maxConfirmableHours={Math.min(row.hoursSubmitted, nteRemaining)}
              expertName={expertName}
            />
          ))}
        </HoursTable>
      )}
    </Section>
  );
}

/**
 * The NTE headroom a partial outcome may not exceed. Null NTE means no ceiling
 * was ever set, so the submitted hours are the only bound.
 *
 * Rounded to the 2dp the wire carries in both directions: the float
 * subtraction is not exact (40 - 32.8 is 7.199999999999997), and an unrounded
 * bound would both render that noise and reject the value it displays.
 */
function nteHeadroom(view: EngagementView): number {
  return view.nteHoursTotal === null
    ? Infinity
    : Math.round((view.nteHoursTotal - view.nteHoursConsumed) * 100) / 100;
}

/**
 * `submittedAt` descending, then the later id — the api's own `submitted_at, id`
 * order reversed. Parsed rather than string-compared, since RFC 3339 fractional
 * seconds vary in length and do not sort lexically.
 */
function newestFirst(a: HoursSubmissionView, b: HoursSubmissionView): number {
  return (
    Date.parse(b.submittedAt ?? '') - Date.parse(a.submittedAt ?? '') || compareIds(b.id, a.id)
  );
}

const NUMERIC_ID = /^\d+$/;

/**
 * Real ids are numeric strings (`FormatInt`) and compare by value, so `100`
 * follows `99`. Mock and fixture ids such as `hrs_w14` are not numbers, and
 * `Number` would make them NaN, which `sort` reads as a tie and leaves in wire
 * order, so those compare as strings instead.
 */
function compareIds(a: string, b: string): number {
  if (NUMERIC_ID.test(a) && NUMERIC_ID.test(b)) return Number(a) - Number(b);
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
